import { Button, buttonVariants } from '@worldcoo/ui/components/button';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { useLingui } from '@lingui/react/macro';
import { cn, formatNumber, campaignImageSize } from '@worldcoo/ui/lib/utils';
import { Link, useNavigate, useSearch, useRouteContext, isNotFound } from '@tanstack/react-router';
import { CheckIcon, ListFilterIcon, SearchIcon } from 'lucide-react';
import { lazy, Suspense, type ReactNode } from 'react';
import { Input } from '@worldcoo/ui/components/input';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { NotFoundIcon } from './assets/not-found-icon';
import { StateFilter } from './campaigns-filters-sidebar';
import { Image } from '../../-components/image';
import {
	usePortalAuthorQuery,
	usePortalCampaignsQuery,
	usePortalMetadataQuery,
	usePortalCampaignsAggregatesQuery,
} from '@/lib/data-hooks';
import { Progress } from '@worldcoo/ui/components/progress';
import { useFilterPanel } from './filter-panel';
import { useIsFilterApplied } from '@/lib/use-filter-applied';
import { usePortalDisplayContext } from './portal-display-context';

const VideoEmbed = lazy(() => import('./video-embed').then((m) => ({ default: m.VideoEmbed })));

export function Campaigns() {
	const { t } = useLingui();
	const portalData = usePortalDisplayContext();
	if (portalData) {
		return (
			<CampaignsContent>
				<DivWithPrimaryColorVariable>
					<div className="flex flex-col gap-3">
						<h2 className="text-4xl font-medium">{t`Campaigns`}</h2>
						<p>{t`Explore our initiatives and choose the cause that inspires you most`}</p>
					</div>
					<ul className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
						<div className="bg-background shadow-3xl col-span-full flex w-full flex-col items-center justify-center gap-6 rounded-xl p-5 sm:p-6">
							<NotFoundIcon className="text-primary" />
							<span className="text-lg font-medium">{t`No campaigns`}</span>
						</div>
					</ul>
				</DivWithPrimaryColorVariable>
			</CampaignsContent>
		);
	}

	return (
		<CampaignsContent>
			<Suspense fallback={<CampaignsGridPending />}>
				<SuspensedCampaigns />
			</Suspense>
		</CampaignsContent>
	);
}

/** Also part of the home route's `pendingComponent` skeleton; see page-pending.tsx. */
export function CampaignsGridPending() {
	return (
		<div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
			{Array.from({ length: 9 }).map((_, index) => (
				// oxlint-disable-next-line react/no-array-index-key
				<Skeleton key={index} className="shadow-3xl h-96 rounded-xl" />
			))}
		</div>
	);
}

// TODO: revisit this later
function CampaignsContent({ children }: { children: ReactNode }) {
	return children;
}

function SuspensedCampaigns() {
	const { t } = useLingui();
	const navigate = useNavigate({ from: '/$portalId' });
	const { q } = useSearch({ from: '/$portalId/' });
	const filterPanel = useFilterPanel();

	const isFilterApplied = useIsFilterApplied();
	const data = usePortalCampaignsQuery();
	const metadata = usePortalMetadataQuery();
	const aggregates = usePortalCampaignsAggregatesQuery();

	if (isNotFound(data)) return null;

	const { campaigns, campaignsCount, activeCampaignsCount } = data;
	const finishedCampaignsCount = campaignsCount - activeCampaignsCount;
	const showNgos = useShowNgos(campaigns);

	return (
		<>
			<div className="flex flex-col gap-3">
				<h2 className="text-4xl font-medium">{t`Campaigns`}</h2>
				<p>{t`Explore our initiatives and choose the cause that inspires you most`}</p>
			</div>
			{activeCampaignsCount + finishedCampaignsCount > 1 && (
				<DivWithPrimaryColorVariable className="flex flex-col-reverse items-center justify-between gap-3 lg:flex-row">
					<StateFilter />

					<div className="z-10 flex w-full gap-3 lg:w-auto">
						<div className="relative w-full lg:w-auto">
							<SearchIcon className="text-muted-foreground pointer-events-none absolute m-1.5 p-0.5" />
							<Input
								className="bg-background! rounded-sm pl-10 shadow-none lg:max-w-60"
								placeholder={t`Search anything`}
								value={q}
								onChange={(e) => navigate({ search: { q: e.target.value }, resetScroll: false })}
							/>
						</div>

						<Button
							variant={isFilterApplied ? 'outlinePrimary' : 'outline'}
							size="icon"
							className="bg-background! hover:bg-accent! relative cursor-pointer rounded-sm shadow-none"
							onClick={() => filterPanel.toggle()}
						>
							<ListFilterIcon />
							<span className="sr-only">{t`Filter`}</span>

							{isFilterApplied && (
								<div className="border-background absolute -top-1 -right-1 size-3 rounded-full border bg-red-500" />
							)}
						</Button>
					</div>
				</DivWithPrimaryColorVariable>
			)}

			<DivWithPrimaryColorVariable>
				<ul className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
					{campaigns.length === 0 && (
						<div className="bg-background shadow-3xl col-span-full flex w-full flex-col items-center justify-center gap-6 rounded-xl p-5 sm:p-6">
							<NotFoundIcon className="text-primary" />
							<span className="text-lg font-medium">{t`No campaigns`}</span>
						</div>
					)}
					{campaigns.map((campaign) => (
						<CampaignCard
							key={campaign.id}
							campaign={campaign}
							showNgos={showNgos}
							isSingle={campaigns.length === 1}
							metadata={metadata}
							aggregates={aggregates}
						/>
					))}
				</ul>
			</DivWithPrimaryColorVariable>
		</>
	);
}

function useShowNgos(campaigns: Array<{ ngos: Array<{ id: string }> }>) {
	const author = usePortalAuthorQuery();
	if (isNotFound(author)) return true;
	if (author.type !== 'ngo') return true;
	return !campaigns.every((c) => c.ngos.length === 1 && c.ngos[0]?.id === author.ngoId);
}

type PortalMetadata = ReturnType<typeof usePortalMetadataQuery>;
type PortalAggregates = ReturnType<typeof usePortalCampaignsAggregatesQuery>;

function CampaignCard(props: {
	campaign: {
		id: string;
		slug?: string;
		slugsByLocale: Record<string, string>;
		name?: string;
		heroImageAlt?: string;
		heroVideoUrl?: string;
		heroVideoAlt?: string;
		description?: string;
		media?: { location: string };
		finished: boolean;
		ngos: Array<{ id: string; name?: string; logo?: { location: string; width: number; height: number } }>;
	};
	showNgos: boolean;
	/** Whether this is the only campaign in the list (drives single-card layout). Passed from parent to avoid re-querying. */
	isSingle: boolean;
	metadata: PortalMetadata;
	aggregates: PortalAggregates;
}) {
	const { t } = useLingui();
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const campaignParam = props.campaign.slugsByLocale[locale] ?? props.campaign.id;

	return (
		<li
			className={cn(
				'bg-background shadow-3xl relative flex flex-col gap-3 rounded-xl p-3',
				props.isSingle && 'col-span-full p-6 lg:flex-row lg:gap-10 lg:pr-10',
			)}
		>
			{props.campaign.heroVideoUrl ? (
				<div className={cn('relative overflow-hidden rounded-lg', props.isSingle && 'flex-1')}>
					<CampaignCardMedia campaign={props.campaign} isSingle={props.isSingle} />
					<CampaignCardBadge
						campaignId={props.campaign.id}
						finished={props.campaign.finished}
						aggregates={props.aggregates}
					/>
				</div>
			) : (
				<Link
					to="/$portalId/$campaignId"
					params={{ portalId, campaignId: campaignParam }}
					search={(prev) => prev}
					className={cn('flex flex-col gap-3', props.isSingle && 'flex-1')}
				>
					<div className="relative overflow-hidden rounded-lg">
						<CampaignCardMedia campaign={props.campaign} isSingle={props.isSingle} />
						<CampaignCardBadge
							campaignId={props.campaign.id}
							finished={props.campaign.finished}
							aggregates={props.aggregates}
						/>
					</div>
				</Link>
			)}

			<div className={cn('flex flex-col gap-4', props.isSingle ? 'py-4 lg:w-1/3' : 'flex-1')}>
				<Link
					to="/$portalId/$campaignId"
					params={{ portalId, campaignId: campaignParam }}
					search={(prev) => prev}
					className="mb-auto flex flex-col gap-3"
				>
					<h3 className="text-lg leading-snug">{props.campaign.name}</h3>

					{props.campaign.description && (
						<div
							className={cn(
								'text-muted-foreground **:text-muted-foreground! **:font-normal! text-sm',
								props.isSingle && 'text-base',
							)}
							dangerouslySetInnerHTML={{ __html: props.campaign.description }}
						/>
					)}
				</Link>

				{/**
				 * MARK: NGOs list
				 **/}
				{portalId !== 'eroski' && props.showNgos && (
					<div className="mb-3 flex flex-col gap-1">
						<span>{t`A campaign from: `}</span>
						<div className="flex h-[50px] items-center gap-3">
							{props.campaign.ngos
								.filter((ngo) => ngo.logo !== undefined)
								.map((ngo) => (
									<Image
										key={ngo.id}
										src={ngo.logo!.location}
										height={50}
										aspectRatio={ngo.logo!.width / ngo.logo!.height}
										alt={ngo.name}
									/>
								))}
						</div>
					</div>
				)}

				<CampaignCardProgress campaignId={props.campaign.id} metadata={props.metadata} aggregates={props.aggregates} />

				<Link
					to={!props.campaign.finished ? '/$portalId/$campaignId/donate' : '/$portalId/$campaignId'}
					params={{ portalId, campaignId: campaignParam }}
					search={(prev) => prev}
					className={cn(
						buttonVariants({ size: 'xl' }),
						props.campaign.finished && 'text-primary bg-primary/10 hover:bg-primary/7',
					)}
				>
					{!props.campaign.finished ? t`Donate` : t`View campaign`}
				</Link>
			</div>
		</li>
	);
}

function CampaignCardMedia(props: {
	campaign: { media?: { location: string }; heroVideoUrl?: string; heroImageAlt?: string; name?: string };
	isSingle: boolean;
}) {
	const { campaign, isSingle } = props;

	if (campaign.heroVideoUrl) {
		return (
			<Suspense>
				<VideoEmbed
					url={campaign.heroVideoUrl}
					title={campaign.heroImageAlt ?? campaign.name ?? 'Campaign video'}
					className={cn('w-full', isSingle ? 'aspect-[796/398]' : campaignImageSize.className)}
				/>
			</Suspense>
		);
	}

	if (campaign.media) {
		return (
			<Image
				src={campaign.media.location}
				height={isSingle ? 398 : campaignImageSize.height}
				aspectRatio={isSingle ? 796 / 398 : campaignImageSize.width / campaignImageSize.height}
				alt={campaign.heroImageAlt ?? campaign.name}
				priority={isSingle}
				className={cn(
					'w-full min-w-full object-cover',
					isSingle ? 'aspect-796/398 md:h-[398px]' : campaignImageSize.className,
				)}
			/>
		);
	}

	return null;
}

function CampaignCardBadge(props: { campaignId: string; finished: boolean; aggregates: PortalAggregates }) {
	return (
		<Suspense fallback={<CampaignCardBadgeFallback finished={props.finished} />}>
			<SuspensedCampaignCardBadge
				campaignId={props.campaignId}
				finished={props.finished}
				aggregates={props.aggregates}
			/>
		</Suspense>
	);
}

function CampaignCardBadgeFallback(props: { finished: boolean }) {
	const { t } = useLingui();

	if (!props.finished) return null;

	return (
		<div className="bg-background absolute top-3 left-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold shadow-sm">
			<CheckIcon className="size-5 rounded-full bg-green-600 p-1 text-white" strokeWidth={4} />
			{t`Campaign funded`}
		</div>
	);
}

function SuspensedCampaignCardBadge(props: { campaignId: string; finished: boolean; aggregates: PortalAggregates }) {
	const { t } = useLingui();
	const { locale } = useRouteContext({ from: '__root__' });

	if (!props.aggregates || !('aggregates' in props.aggregates)) return null;

	const campaignAggregate = props.aggregates.aggregates[props.campaignId];
	if (!campaignAggregate) return null;

	const progress = campaignAggregate.progress ?? 0;
	const funding = campaignAggregate.funding;

	return (
		<>
			{props.finished && (
				<div className="bg-background absolute top-3 left-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold shadow-sm">
					<CheckIcon className="size-5 rounded-full bg-green-600 p-1 text-white" strokeWidth={4} />
					{t`Campaign funded`}
				</div>
			)}

			{!props.finished && funding && (
				<div className="bg-background absolute top-3 left-3 rounded-lg px-3 py-2 text-sm font-semibold shadow-sm">
					{(() => {
						const funded = formatNumber(locale, progress, 0);
						return t`${funded}% Funded`;
					})()}
				</div>
			)}
		</>
	);
}

function CampaignCardProgress(props: { campaignId: string; metadata: PortalMetadata; aggregates: PortalAggregates }) {
	return (
		<Suspense fallback={<CampaignCardProgressFallback />}>
			<SuspensedCampaignCardProgress
				campaignId={props.campaignId}
				metadata={props.metadata}
				aggregates={props.aggregates}
			/>
		</Suspense>
	);
}

function CampaignCardProgressFallback() {
	return (
		<div className="flex flex-col gap-1">
			<Skeleton className="h-1.5 w-full" />
			<div className="flex items-center gap-2">
				<Skeleton className="h-7 w-20" />
				<Skeleton className="h-5 w-32" />
			</div>
		</div>
	);
}

function SuspensedCampaignCardProgress(props: {
	campaignId: string;
	metadata: PortalMetadata;
	aggregates: PortalAggregates;
}) {
	const { t } = useLingui();
	const { locale } = useRouteContext({ from: '__root__' });

	if (isNotFound(props.metadata)) return null;
	if (!props.aggregates || !('aggregates' in props.aggregates)) return null;
	if (props.metadata.hideCollected) return null;

	const campaignAggregate = props.aggregates.aggregates[props.campaignId];
	if (!campaignAggregate) return null;

	if (props.metadata.hideStatsOnActiveCampaigns && !campaignAggregate.finished) {
		return null;
	}

	const finished = campaignAggregate.finished;
	const progress = campaignAggregate.progress ?? 0;
	const collected = campaignAggregate.collected ?? 0;
	const funding = campaignAggregate.funding;

	if (!funding) return null;

	return (
		<div className="flex flex-col gap-1">
			{!finished &&
				collected < funding &&
				(() => {
					return <Progress value={progress} className="h-1.5" aria-label={t`${progress}% Funded`} />;
				})()}
			<div className="flex items-center gap-2">
				<span className="text-primary text-lg font-semibold">{formatNumber(locale, collected) + ' €'}</span>
				<span className="text-muted-foreground text-sm">
					{(() => {
						const goal = formatNumber(locale, funding, 0);
						return t`Goal of ${goal}` + ' €';
					})()}
				</span>
			</div>
		</div>
	);
}
